import { useEffect, useState } from "react";
import cn from "classnames";
import { useAppDispatch } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import { CloseIcon } from "shared/ui/Icons";
import { Portal } from "shared/ui/Portal";
import s from "./UpdateAllCardsModal.module.scss";

import { updateAllCardsModalStore } from "../model/updateAllCardsModal.store";

type UpdateAllCardsModalProps = {
  updateAllCards: () => void;
  canCloseModal: boolean;
  progress: {
    total: number;
    current: number;
  };
  results: { reference: string | null; isSuccess: boolean }[];
  resetState: () => void;
};

export const UpdateAllCardsModal: RFC<UpdateAllCardsModalProps> = ({
  updateAllCards,
  canCloseModal,
  progress,
  results,
  resetState,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [animatedWidth, setAnimatedWidth] = useState(0);

  const percentage = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  const isLoaded = progress.current === progress.total;

  useEffect(() => {
    const idTimeout = setTimeout(() => {
      setAnimatedWidth(percentage);
    }, 100);

    return () => clearTimeout(idTimeout);
  }, [percentage]);

  const closeModal = () => {
    if (canCloseModal) {
      dispatch(updateAllCardsModalStore.actions.closeUpdateAllCardsModalAction());
      resetState();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const renderModalContent = () => {
    const shouldRenderInitialContent = canCloseModal && !results.length;
    const shouldRenderLoadingContent =
      (!canCloseModal && progress.current < progress.total) || progress.current === progress.total;
    const shouldRenderAcceptContent = canCloseModal;

    if (shouldRenderInitialContent) {
      return (
        <>
          <Button className={s.closeBtn} onClick={closeModal}>
            <CloseIcon />
          </Button>
          <div className={s.contentBox}>
            <div className={s.title}>{t("Are you sure you want to update all shipments in one go?")}</div>
            <div className={s.description}>
              {t("Shipments which were not tracked this month, will consume credits")}.
            </div>
            <div className={s.buttonsWrapper}>
              <Button className={s.cancelButton} onClick={closeModal} size="small">
                {t("No, cancel")}
              </Button>
              <Button className={s.updateButton} variant="primary" onClick={updateAllCards} size="small">
                {t("Yes, update all")}
              </Button>
            </div>
          </div>
        </>
      );
    }
    if (shouldRenderLoadingContent) {
      return (
        <>
          <div className={s.contentBox}>
            <Button className={s.closeBtn} onClick={closeModal}>
              <CloseIcon />
            </Button>
            {progress.current === progress.total ? (
              <>
                <div className={s.title}>{t("All shipments have been updated")}</div>
                <div className={s.description}>
                  {`${t("All")} ${progress.total} ${t("shipments have been successfully updated")}`}.
                </div>
                <div className={s.progressText}>
                  {progress.current}/{progress.total} {t("shipments done")}
                </div>
              </>
            ) : (
              <>
                <div className={s.title}>{t("Updating all shipments")}</div>
                <div className={s.description}>{t("This may take a while, please wait")}.</div>
                <div className={s.progressText}>
                  {progress.current}/{progress.total} {t("shipments processed")}
                </div>
              </>
            )}
            <div className={s.progressContainer}>
              <div
                style={{ width: `${animatedWidth}%` }}
                className={cn(s.progressBar, {
                  [s.progressLoading]: !isLoaded,
                  [s.progressLoaded]: isLoaded,
                })}
              />
            </div>
            <Button
              className={cn(s.buttonOk, {
                [s.buttonOkLoading]: !isLoaded,
                [s.buttonOkLoaded]: isLoaded,
              })}
              onClick={closeModal}
              size="small"
            >
              {t("Okay")}
            </Button>
          </div>
        </>
      );
    }

    if (shouldRenderAcceptContent) {
      const hasErrors = results.filter((result) => !result.isSuccess);
      const successCount = results.filter((result) => result.isSuccess).length;

      if (hasErrors.length) {
        return (
          <>
            <Button className={s.closeBtn} onClick={closeModal}>
              <CloseIcon />
            </Button>
            <div className={s.contentBox}>
              <div className={s.title}>{t("Some shipments couldn’t be updated")}</div>
              <div className={s.description}>
                {`${successCount} ${t("shipments were updated successfully, but a few couldn’t be processed at this time.")}`}
              </div>

              <div className={s.errorTitle}>{t("The following shipments were not updated")}:</div>
              <ul className={s.errorList}>
                {hasErrors.map((error) => (
                  <li key={error.reference}>
                    {error.reference}
                    <div className={s.copyButton} onClick={() => copyToClipboard(error.reference || "")}>
                      {t("Copy")}
                    </div>
                  </li>
                ))}
              </ul>

              <Button className={s.buttonOk} variant="primary" onClick={closeModal} size="small">
                {t("Okay")}
              </Button>
            </div>
          </>
        );
      }
    }
  };

  return (
    <Portal className={s.modal} closePopup={closeModal}>
      {renderModalContent()}
    </Portal>
  );
};
